import api from './axios'

/**
 * Get all contracts with filters
 * @param {object} params - Filter parameters
 * @returns {Promise<{data: Array, pagination: object}>}
 */
export const getContracts = async (params = {}) => {
  const response = await api.get('/contracts', { params })
  return response.data
}

/**
 * Get single contract by ID
 * @param {number} id 
 * @returns {Promise<object>}
 */
export const getContract = async (id) => {
  const response = await api.get(`/contracts/${id}`)
  return response.data
}

/**
 * Get contract with full details
 * @param {number} id 
 * @returns {Promise<object>}
 */
export const getContractFull = async (id) => {
  const response = await api.get(`/contracts/${id}/full`)
  return response.data
}

/**
 * Create new contract
 * @param {object} contractData
 * @param {File} file - Optional contract file
 * @returns {Promise<object>}
 */
export const createContract = async (contractData, file = null) => {
  // If file is provided, use FormData
  if (file) {
    const formData = new FormData()
    
    // Append all contract data fields
    Object.keys(contractData).forEach(key => {
      if (contractData[key] !== null && contractData[key] !== undefined) {
        // Handle arrays (like trainer_ids)
        if (Array.isArray(contractData[key])) {
          formData.append(key, JSON.stringify(contractData[key]))
        } else {
          formData.append(key, contractData[key])
        }
      }
    })
    
    // Append file
    formData.append('contractFile', file)
    
    const response = await api.post('/contracts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }
  
  // Otherwise send as JSON
  const response = await api.post('/contracts', contractData)
  return response.data
}

/**
 * Update contract
 * @param {number} id 
 * @param {object} contractData 
 * @returns {Promise<object>}
 */
export const updateContract = async (id, contractData) => {
  const response = await api.put(`/contracts/${id}`, contractData)
  return response.data
}

/**
 * Delete contract
 * @param {number} id 
 * @returns {Promise<void>}
 */
export const deleteContract = async (id) => {
  const response = await api.delete(`/contracts/${id}`)
  return response.data
}

/**
 * Upload contract file
 * @param {number} id
 * @param {File} file
 * @returns {Promise<object>}
 */
export const uploadContractFile = async (id, file) => {
  const formData = new FormData()
  formData.append('contractFile', file)
  
  const response = await api.post(`/contracts/${id}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

/**
 * Get contract trainers
 * @param {number} id
 * @returns {Promise<Array>}
 */
export const getContractTrainers = async (id) => {
  const response = await api.get(`/contracts/${id}/trainers`)
  return response.data
}

/**
 * Send signing link to trainer(s)
 * @param {number} id - Contract ID
 * @param {object} data - { trainer_ids?, email_subject?, email_message? }
 * @returns {Promise<object>}
 */
export const sendSigningLink = async (id, data = {}) => {
  const response = await api.post(`/contracts/${id}/send-link`, data)
  return response.data
}

/**
 * Send reminder to trainer(s)
 * @param {number} id - Contract ID
 * @param {object} data - { trainer_ids?, email_subject?, email_message? }
 * @returns {Promise<object>}
 */
export const remindSigningLink = async (id, data = {}) => {
  const response = await api.post(`/contracts/${id}/remind`, data)
  return response.data
}

/**
 * Get preview of signing link email
 * @param {number} id - Contract ID
 * @param {object} params - { trainer_id?, is_reminder? }
 * @returns {Promise<object>}
 */
export const getSigningLinkPreview = async (id, params = {}) => {
  const response = await api.get(`/contracts/${id}/send-link-preview`, { params })
  return response.data
}

/**
 * Download contract file
 * @param {number} id
 * @returns {Promise<Blob>}
 */
export const downloadContract = async (id) => {
  const response = await api.get(`/contracts/${id}/download`, {
    responseType: 'blob'
  })
  return response.data
}

/**
 * Download original (unsigned) contract file
 * @param {number} id
 * @returns {Promise<Blob>}
 */
export const downloadOriginalContract = async (id) => {
  const response = await api.get(`/contracts/${id}/download-original`, {
    responseType: 'blob'
  })
  return response.data
}